import { Box } from '@ironhack/design-system2/components/box';
import { Icon } from '@ironhack/design-system2/components/icon';
import { type IconProps as FeatherIconProps } from '@ironhack/design-system2/icons';
import { Minus } from '@ironhack/design-system2/icons/minus';
import dynamic from 'next/dynamic';
import { type ComponentType, type ReactElement } from 'react';

import { type DatoIconBlock } from '@/lib/datocms/types';
import { toUpperPascalCase } from '@/lib/utils';

type DynamicIconProps = Pick<Required<DatoIconBlock>, 'color' | 'name'>;

const getIconComponent = ({ color, name }: DynamicIconProps): ComponentType =>
  dynamic(
    async () => {
      const mod = await import('@ironhack/design-system2/icons');
      const StyledIcon = (): ReactElement | null => {
        const iconName = toUpperPascalCase(name);
        const IconAsset = mod[iconName as keyof typeof mod];
        return IconAsset ? (
          <Icon
            as={IconAsset as ComponentType<FeatherIconProps>}
            boxSize="24px"
            {...(color && { color: `icon.${color}` })}
          />
        ) : null;
      };
      return StyledIcon;
    },
    {
      loading: (): ReactElement => (
        <Icon
          as={Minus}
          boxSize="24px"
          {...(color && { color: `icon.${color}` })}
        />
      ),
    }
  );

type Props = Pick<Required<DatoIconBlock>, '__typename' | 'color' | 'name'>;

export const OptionIcon = (props: Props): ReactElement | null => {
  const { __typename: typename, color, name } = props;

  switch (typename) {
    case 'IconColorRecord': {
      return (
        <Box background={`icon.${color}`} borderRadius="full" boxSize="16px" />
      );
    }

    case 'IconRecord': {
      const DynamicIcon = getIconComponent({ color, name });
      return <DynamicIcon />;
    }

    default: {
      return null;
    }
  }
};
